<template>
	<div class="options-tags">
		<div class="caption applied-tags" v-if="isFilteredByTags">
			You see only products matching selected step criteria:
			<span v-for="tags in activeWorkflowTags" :key="tags.group" class="tag-group">
				<span v-if="tags.tags.length">
					<span class="caption-2">{{ tags.group }}</span> -
					<span v-for="tag in tags.tags" :key="tag" class="tag-name">{{ tag }}</span>
				</span>
			</span>
			<span v-for="tags in activeStepTags" :key="tags.group" class="tag-group">
				<span v-if="tags.tags.length">
					<span class="caption-2">{{ tags.group }}</span> -
					<span v-for="tag in tags.tags" :key="tag" class="tag-name">{{ tag }}</span>
				</span>
			</span>
		</div>
		<div class="options-group" v-for="group in displayedTagGroups" :key="group.id" :data-test="`option-tag-group-${group.name}`">
			<div class="caption">{{ group.name }}</div>

			<div class="options-group-tags">
				<div
					class="options-tag caption-2"
					:class="{active: isTagActive(tag)}"
					v-for="tag in group.tags"
					:key="tag.id"
					@click="selectTag(tag)"
					:data-test="`tag-${tag.name}`"
				>
					{{ tag.name }}
				</div>
			</div>
		</div>
	</div>
</template>
<script>
export default {
	name: 'OptionsTags',
	props: {
		options: {
			type: Object,
		},
	},
	computed: {
		isFilteredByTags(){
			return this.activeWorkflowTags.reduce((acc,group)=>{acc.push(...group.tags); return acc;} ,[]).length>0
        || this.activeStepTags.reduce((acc,group)=>{acc.push(...group.tags); return acc;} ,[]).length>0;
		},
		activeWorkflowTags() {
			const uniqueAssignedTags = this.$store.getters['tags/uniqueAssignedTags'];
			const displayedTags = this.$store.getters['tags/uniqueAssignedTagGroups']('WORKFLOW').reduce(
				(acc, group) => {
					//filter groups with less than 2 tags in current workflow, sorted by order in group.tags
					let existingTags = group.tags.reduce((tags, tagId) => {
						return [...tags, ...uniqueAssignedTags.filter((tag) => tag.id === tagId)];
					}, []);
					return existingTags.length > 1 ? [...acc, {...group, tags: existingTags}] : acc;
				},
				[],
			);
			const activeTags = this.$store.getters['tags/activeWorkflowTags'];
			if (!activeTags) return [];
			return Object.keys(activeTags).reduce((acc, groupId) => {
				const displayedGroup = displayedTags.find((v) => v.id.toString() === groupId);
				if (displayedGroup) {
					const activeTagsInGroup = Array.from(activeTags[groupId]);
					const tagNames = activeTagsInGroup.map(
						(tagId) => displayedGroup.tags.find((tag) => tag.id === tagId).name,
					);
					acc.push({group: displayedGroup.name, tags: tagNames});
				}
				return acc;
			}, []);
		},
		activeStepTags() {
			const uniqueAssignedTags = this.$store.getters['tags/uniqueAssignedTags'];
			const displayedTags = this.$store.getters['tags/uniqueAssignedTagGroups']('STEP').reduce((acc, group) => {
				//filter groups with less than 2 tags in current workflow, sorted by order in group.tags
				let existingTags = group.tags.reduce((tags, tagId) => {
					return [...tags, ...uniqueAssignedTags.filter((tag) => tag.id === tagId)];
				}, []);
				return existingTags.length > 1 ? [...acc, {...group, tags: existingTags}] : acc;
			}, []);
			const activeTags = this.$store.getters['tags/activeStepTags'][this.options.name];
			if (!activeTags) return [];
			return Object.keys(activeTags).reduce((acc, groupId) => {
				const displayedGroup = displayedTags.find((v) => v.id.toString() === groupId);
				if (displayedGroup) {
					const activeTagsInGroup = Array.from(activeTags[groupId]);
					const tagNames = activeTagsInGroup.map(
						(tagId) => displayedGroup.tags.find((tag) => tag.id === tagId).name,
					);
					acc.push({group: displayedGroup.name, tags: tagNames});
				}
				return acc;
			}, []);
		},
		tags() {
			let products = this.options.products;
			let productsTagsIds = products
				.map((p) => this.$store.getters['tags/productTagAssignments'](p).map((tag) => tag.id))
				.flat();
			let uniqueProductsTagsIds = new Set(productsTagsIds);
			return Array.from(uniqueProductsTagsIds).map((id) => this.$store.getters['tags/tags'][id]);
		},
		tagGroups() {
			return this.$store.getters['tags/uniqueAssignedTagGroups']('ALTERNATIVE_PRODUCTS');
		},
		displayedTagGroups() {
			let displayedTagGroups = this.tagGroups.reduce((acc, group) => {
				//filter groups with less than 2 tags in avilable alternatives, sorted by order in group.tags
				let existingTags = group.tags.reduce((tags, tagId) => {
					return [...tags, ...this.tags.filter((tag) => tag.id === tagId)];
				}, []);
				return existingTags.length > 1 ? [...acc, {...group, tags: existingTags}] : acc;
			}, []);
			return displayedTagGroups;
		},
		activeTags() {
			return this.$store.getters['tags/activeAlternativeProductsTags'];
		},
	},
	methods: {
		isTagActive(tag) {
			let retval = Object.values(this.activeTags).some((tagGroup) => tagGroup.has(tag.id));
			return retval;
		},
		selectTag(tag) {
			this.isTagActive(tag)
				? this.$store.dispatch('tags/unsetActiveAlternativeProductsTag', {tag})
				: this.$store.dispatch('tags/setActiveAlternativeProductsTag', {tag});
		},
	},
};
</script>
<style scoped lang="scss">
.options-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 0 20px;
	.applied-tags {
		margin-top: 16px;
		width: 100%;
		.tag-group {
			margin: 0 4px;
			.tag-name:not(:last-child)::after {
				content: ', ';
			}
		}
	}
	.options-group {
		margin-top: 16px;
		.options-group-tags {
			@include flexRow;
			flex-wrap: wrap;
			.options-tag {
				white-space: nowrap;
				border: 1px solid;
				border-radius: 10px;
				border-color: $color-text-grey;
				padding: 4px 8px;
				margin: 4px 8px 4px 0;
				cursor: pointer;
				&.active {
					border-color: $color-blue;
					border-width: 2px;
					background: $color-super-light-blue;
					color: $color-blue;
				}
			}
		}
	}
}
</style>
